/**
 * Stack360 Compression Utility - Final Version
 * 
 * This utility handles reading and validation of .stack360 files on the frontend.
 * It provides a balance between thorough validation and reliability.
 */

class Stack360CompressionUtil {
  static MAGIC_NUMBER = new Uint8Array([83, 51, 54, 48]); // 'S360' in bytes
  static VERSION = 1;
  static COMPRESSION_GZIP = 1;
  static HEADER_SIZE = 10;

  /**
   * Check if a file is a valid .stack360 file
   * @param {File|ArrayBuffer} file - The file to check
   * @returns {Promise<boolean>} - True if valid .stack360 file
   */
  static async isValidStack360File(file) {
    try {
      let buffer;
      
      if (file instanceof File) {
        // Only read the first part of the file to check header
        const headerBlob = file.slice(0, this.HEADER_SIZE);
        const arrayBuffer = await headerBlob.arrayBuffer();
        buffer = new Uint8Array(arrayBuffer);
      } else if (file instanceof ArrayBuffer) {
        buffer = new Uint8Array(file);
      } else {
        return false;
      }

      if (buffer.length < this.HEADER_SIZE) {
        return false;
      }

      // Check magic number
      const magicNumber = buffer.slice(0, 4);
      const magicMatches = this.MAGIC_NUMBER.every((byte, index) => byte === magicNumber[index]);
      
      if (!magicMatches) {
        return false;
      }

      // Check version
      const version = buffer[4];
      if (version !== this.VERSION) {
        return false;
      }

      // Check compression method
      const compressionMethod = buffer[5];
      if (compressionMethod !== this.COMPRESSION_GZIP) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking Stack360 file:', error);
      return false;
    }
  }

  /**
   * Get file metadata without decompressing (safe version)
   * @param {File} file - The .stack360 file
   * @returns {Promise<Object>} - File metadata and info
   */
  static async getFileInfo(file) {
    try {
      if (!(await this.isValidStack360File(file))) {
        throw new Error('Invalid .stack360 file');
      }

      // Read a larger chunk to get metadata
      const chunkSize = Math.min(file.size, 1024); // Read first 1KB
      const headerBlob = file.slice(0, chunkSize);
      const arrayBuffer = await headerBlob.arrayBuffer();
      const buffer = new Uint8Array(arrayBuffer);

      // Read metadata length (bytes 6-9, little endian)
      const metadataLength = new DataView(arrayBuffer, 6, 4).getUint32(true);
      
      // Validate metadata length is reasonable
      if (metadataLength <= 0 || metadataLength > 512) { // Max 512 bytes for metadata
        throw new Error('Invalid or too large metadata');
      }

      // Check if we have enough data in our chunk
      const metadataEnd = this.HEADER_SIZE + metadataLength;
      if (metadataEnd > buffer.length) {
        // Read more data if needed
        const largerBlob = file.slice(0, metadataEnd);
        const largerBuffer = await largerBlob.arrayBuffer();
        const newBuffer = new Uint8Array(largerBuffer);
        
        if (metadataEnd > newBuffer.length) {
          throw new Error('File too small for declared metadata size');
        }
        
        const metadataBytes = newBuffer.slice(this.HEADER_SIZE, metadataEnd);
        const metadataText = new TextDecoder().decode(metadataBytes);
        const metadata = JSON.parse(metadataText);

        return {
          isValid: true,
          version: newBuffer[4],
          compressionMethod: newBuffer[5],
          fileSize: file.size,
          metadata,
          originalSize: metadata.originalSize,
          compressionRatio: metadata.originalSize ? 
            ((1 - file.size / metadata.originalSize) * 100).toFixed(2) + '%' : 'Unknown'
        };
      } else {
        // We have enough data in the first chunk
        const metadataBytes = buffer.slice(this.HEADER_SIZE, metadataEnd);
        const metadataText = new TextDecoder().decode(metadataBytes);
        const metadata = JSON.parse(metadataText);

        return {
          isValid: true,
          version: buffer[4],
          compressionMethod: buffer[5],
          fileSize: file.size,
          metadata,
          originalSize: metadata.originalSize,
          compressionRatio: metadata.originalSize ? 
            ((1 - file.size / metadata.originalSize) * 100).toFixed(2) + '%' : 'Unknown'
        };
      }
    } catch (error) {
      // Fallback to basic info if metadata reading fails
      console.warn('Could not read Stack360 metadata, using fallback:', error.message);
      return {
        isValid: true,
        version: this.VERSION,
        compressionMethod: this.COMPRESSION_GZIP,
        fileSize: file.size,
        metadata: {
          name: 'Unknown',
          timestamp: new Date().toISOString(),
          originalSize: null
        },
        originalSize: null,
        compressionRatio: 'Unknown'
      };
    }
  }

  /**
   * Detect file type and return appropriate info
   * @param {File} file - The file to analyze
   * @returns {Promise<Object>} - File type and info
   */
  static async detectFileType(file) {
    // Check for .stack360 files first
    if (file.name.toLowerCase().endsWith('.stack360')) {
      const isStack360 = await this.isValidStack360File(file);
      
      if (isStack360) {
        const info = await this.getFileInfo(file);
        return {
          type: 'stack360',
          extension: '.stack360',
          isCompressed: true,
          ...info
        };
      } else {
        return {
          type: 'stack360',
          extension: '.stack360',
          isCompressed: true,
          isValid: false,
          error: 'Invalid .stack360 file format'
        };
      }
    }
    
    // Check for JSON files
    if (file.name.toLowerCase().endsWith('.json')) {
      try {
        // For large files, only read a sample to validate structure
        const maxSampleSize = 10 * 1024; // 10KB sample
        let textToCheck;
        
        if (file.size > maxSampleSize) {
          const blob = file.slice(0, maxSampleSize);
          textToCheck = await blob.text();
        } else {
          textToCheck = await file.text();
        }
        
        // Try to parse the JSON (or partial JSON)
        let json;
        try {
          json = JSON.parse(textToCheck);
        } catch (partialError) {
          // If it's a partial read and fails, assume it's valid JSON
          if (file.size > maxSampleSize) {
            return {
              type: 'json',
              extension: '.json',
              isCompressed: false,
              isValid: true,
              fileSize: file.size,
              metadata: {
                name: 'Large JSON File',
                code: 'Unknown',
                description: 'File too large to preview',
                objectCount: 'Unknown'
              }
            };
          } else {
            throw partialError;
          }
        }
        
        // Validate basic structure - support multiple formats
        let layout, layoutObjects;
        
        // Check for new export format: { data: { layout, layoutObjects } }
        if (json.data && json.data.layout && json.data.layoutObjects) {
          layout = json.data.layout;
          layoutObjects = json.data.layoutObjects;
        }
        // Check for direct format: { layout, layoutObjects }
        else if (json.layout && json.layoutObjects) {
          layout = json.layout;
          layoutObjects = json.layoutObjects;
        }
        // If we have the basic structure, it's probably valid
        else if (json.data || json.layout || Array.isArray(json)) {
          // Probably a valid warehouse layout file, even if structure is different
          return {
            type: 'json',
            extension: '.json',
            isCompressed: false,
            isValid: true,
            fileSize: file.size,
            metadata: {
              name: 'Unknown Layout',
              code: 'Unknown',
              description: '',
              objectCount: 'Unknown'
            }
          };
        }
        else {
          return {
            type: 'json',
            extension: '.json',
            isCompressed: false,
            isValid: false,
            error: 'Invalid JSON structure for warehouse layout'
          };
        }
        
        return {
          type: 'json',
          extension: '.json',
          isCompressed: false,
          isValid: true,
          fileSize: file.size,
          metadata: {
            name: layout?.name || 'Unknown',
            code: layout?.code || 'Unknown',
            description: layout?.description || '',
            objectCount: Array.isArray(layoutObjects) ? layoutObjects.length : 'Unknown'
          }
        };
      } catch (error) {
        return {
          type: 'json',
          extension: '.json',
          isCompressed: false,
          isValid: false,
          error: `Invalid JSON format: ${error.message}`
        };
      }
    }

    return {
      type: 'unknown',
      isValid: false,
      error: 'Unsupported file type. Please upload a .stack360 or .json file'
    };
  }

  /**
   * Format file size for display
   * @param {number} bytes - Size in bytes
   * @returns {string} - Formatted size string
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validate file before upload
   * @param {File} file - The file to validate
   * @returns {Promise<Object>} - Validation result
   */
  static async validateFile(file) {
    const fileInfo = await this.detectFileType(file);
    
    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File too large. Maximum size is ${this.formatFileSize(maxSize)}`,
        fileInfo
      };
    }

    // Check if file type is supported
    if (!['stack360', 'json'].includes(fileInfo.type)) {
      return {
        isValid: false,
        error: fileInfo.error || 'Unsupported file type. Please upload a .stack360 or .json file',
        fileInfo
      };
    }

    // Check if file structure is valid
    if (!fileInfo.isValid) {
      return {
        isValid: false,
        error: fileInfo.error || 'Invalid file structure',
        fileInfo
      };
    }

    return {
      isValid: true,
      fileInfo
    };
  }
}

export default Stack360CompressionUtil;